import { type ReactElement, type ReactNode } from 'react';
import { UiIcon } from './icons';
import { Skeleton, SKELETON_DELAY_MS, useDelayedFlag } from './skeleton';
import { CountUp } from './charts/count-up';

/**
 * The KPI tile of the Claude Design (D549, DS-CHARTS), in the variants the design draws. Every tile lifts on hover (not only a
 * clickable one): 2px up (`--kpi-hover-lift`), the stronger `--color-border-kpi-hover`, `--shadow-kpi`, 160ms ease-out (`--duration-card`, `--ease-hover`). Values are IBM Plex Mono 700.
 *
 *   record    A08 client record page (the default): r12, label 600 12, value mono 700 20/1.1, hint 500 12. A `subtle` tile is the wallet.
 *   overview  Admin A02 dashboard: r16 padding 20, label 600 12.5, value mono 700 30/1.1, delta text under it.
 *   drawer    the record drawer's three tiles: r10, label 600 11, value mono 700 16.
 *   centered  the FX-05 count-up demo: centred, label mono 600 11 uppercase, value sans 800 32 with tabular numerals.
 *
 * The geometry is `.vello-kpi*` in `charts.css`; colours and the hover come from `@vello/tokens` (`--kpi-*`).
 */

type Tone = 'neutral' | 'success' | 'danger' | 'warning';

export type StatCardVariant = 'record' | 'overview' | 'drawer' | 'centered';

export interface StatDelta {
  /** The change as text, sign included (`+12% vs. last week`, `-3`): the colour and the arrow never carry the meaning alone. */
  readonly text: string;
  readonly direction?: 'up' | 'down' | 'flat';
  /** `success` for a good change, `danger` for a bad one — independent of the direction (fewer cancellations is `down` + `success`). */
  readonly tone?: Tone;
  /** Hidden context for a screen reader (`vs. last week`). */
  readonly srLabel?: string;
  /** Draw a chevron for `direction` before the text. The design's delta is text only, so this is off by default. */
  readonly arrow?: boolean;
}

export interface StatCardProps {
  readonly label: string;
  /** The figure. A string, or an element such as a `CountUp` (D515); for an element pass `mono` so the number stays on one line. */
  readonly value: ReactNode;
  readonly hint?: ReactNode;
  /** The wallet tile of the record page: a subtle (mist) background instead of the card. */
  readonly subtle?: boolean;
  /** Which tile of the design to draw (default `record`). */
  readonly variant?: StatCardVariant;
  readonly tone?: Tone;
  /** Shows skeletons in place of the value and hint, and `aria-busy`. The label stays. */
  readonly loading?: boolean;
  /** How long `loading` must last before the skeleton shows (ms, default 300). */
  readonly loadingDelayMs?: number;
  /** Visually hidden status text while loading (the app's own "Loading" string). */
  readonly loadingLabel?: string;
  /** A small chart slot beside the value (a sparkline from `@vello/web-theme` charts). */
  readonly trend?: ReactNode;
  readonly delta?: StatDelta;
  /** Force the value into (`true`) or out of (`false`) the mono face. Default: mono when the value holds a digit. */
  readonly mono?: boolean;
  /**
   * The value as a NUMBER (D513): the card counts up to it on the first mount (FX-05: 400ms, ease-out cubic) and tweens old → new on a
   * refresh. `value` stays the final text — it decides the mono face and is what a test or a synthetic DOM sees. `decimals` is the
   * decimals the number keeps while counting (a ratio 0.853 shown as 85.3% needs 3).
   */
  readonly count?: { readonly value: number; readonly format: (value: number) => string; readonly decimals?: number };
  /**
   * How a long value fits the tile (owner, 2026-10-13: "17 de set. d" was cut off in the person drawer). `auto` (default): a
   * text value of more than 10 characters holding a word (a date with its month name, a phrase), or any value of more than 16, steps down and wraps instead of being
   * clipped; `wrap` always does; `none` keeps the single nowrap line (an element value such as a `CountUp`).
   */
  readonly fit?: 'auto' | 'wrap' | 'none';
  /** Makes the card a button. */
  readonly onClick?: () => void;
  /** Makes the card a link. */
  readonly href?: string;
}

/**
 * A KPI tile: label, value (mono when it is a number — a word such as "Unavailable" stays in the sans face), optional trend, delta
 * and hint (it wraps; nothing crosses the tile's border). `tone` colours the value with the tone's text role (a breached SLA,
 * documents to resolve) — the label still says what it is, so the state is never colour alone (W6-THEME-CONSOLES, D402).
 */
export function StatCard({ label, value, hint, subtle = false, variant = 'record', tone, loading = false, loadingDelayMs = SKELETON_DELAY_MS, loadingLabel, trend, delta, mono, count, fit = 'auto', onClick, href }: StatCardProps): ReactElement {
  const shown = useDelayedFlag(loading, loadingDelayMs);
  const isMono = mono ?? (typeof value === 'string' && /\d/.test(value));
  const longText = typeof value === 'string' && ((value.length > 10 && /\p{L}{3,}/u.test(value)) || value.length > 16);
  const wraps = count === undefined && (fit === 'wrap' || (fit === 'auto' && longText));
  const interactive = onClick !== undefined || href !== undefined;
  // A plain card keeps its paragraphs; a button / link card may only hold phrasing content, so it uses spans.
  const T = interactive ? 'span' : 'p';
  const cls = ['vello-kpi', variant !== 'record' ? `vello-kpi--${variant}` : '', subtle ? 'vello-kpi--subtle' : '', interactive ? 'vello-kpi--interactive' : ''].filter((c) => c !== '').join(' ');
  const body = loading ? (
    <>
      <T className="vello-kpi__label">{label}</T>
      <span className="vello-loading vello-kpi__loading" data-pending={shown ? 'false' : 'true'}>
        <span className="vello-kpi__main">
          <Skeleton width="45%" height="var(--space-5)" />
        </span>
        {hint !== undefined && <Skeleton width="70%" className="vello-kpi__hint-skeleton" />}
      </span>
      {loadingLabel !== undefined && (
        <span role="status" className="vello-sr-only">
          {loadingLabel}
        </span>
      )}
    </>
  ) : (
    <>
      <T className="vello-kpi__label">{label}</T>
      <span className="vello-kpi__main">
        <T className={`vello-kpi__value${isMono ? ' tnum' : ''}`} data-tone={tone} data-fit={wraps ? 'wrap' : undefined}>
          {count !== undefined ? <CountUp value={count.value} format={count.format} {...(count.decimals !== undefined ? { decimals: count.decimals } : {})} /> : value}
        </T>
        {trend !== undefined && <span className="vello-kpi__trend">{trend}</span>}
      </span>
      {delta !== undefined && (
        <span className="vello-kpi__delta" data-tone={delta.tone} data-direction={delta.direction}>
          {delta.arrow === true && delta.direction === 'up' && <UiIcon name="chevronUp" size={12} />}
          {delta.arrow === true && delta.direction === 'down' && <UiIcon name="chevronDown" size={12} />}
          <span className="tnum">{delta.text}</span>
          {delta.srLabel !== undefined && <span className="vello-sr-only">{delta.srLabel}</span>}
        </span>
      )}
      {hint !== undefined && <T className="vello-kpi__hint">{hint}</T>}
    </>
  );
  if (href !== undefined) {
    return (
      <a className={cls} data-tone={tone} href={href} onClick={onClick} aria-busy={loading || undefined}>
        {body}
      </a>
    );
  }
  if (onClick !== undefined) {
    return (
      <button type="button" className={cls} data-tone={tone} onClick={onClick} aria-busy={loading || undefined}>
        {body}
      </button>
    );
  }
  return (
    <div className={cls} data-tone={tone} aria-busy={loading || undefined}>
      {body}
    </div>
  );
}
